import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MobileAppRanking } from '../rankings/models/mobileAppRanking';
import { SideNavItem } from '../rankings/models/sideNavItem';
import { SideNavItemsMap } from '../rankings/models/sideNavItemsMap';
import { SDK_REPORT_CONFIG } from './SDK_REPORT_CONFIG';
import { MainFilterItem } from '../rankings/models/mainFilterItem';
import { DropdownItem } from '../rankings/models/dropdownItem';
import { SdkIndexService } from 'app/services/sdk-index.service';
import { SdkInsight } from 'app/models/SdkInsight';
import { ReportConfig } from '../rankings/REPORT_CONFIG';
import { RankingsPageBaseComponent } from '../rankings-page-base.component';

@Component({
  selector: 'app-sdk-index',
  templateUrl: './sdk-index.component.html',
  styleUrls: ['./sdk-index.component.scss']
})
export class SdkIndexComponent
  extends RankingsPageBaseComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  currentConfig: ReportConfig;
  gridData: Array<MobileAppRanking>;
  sideNavItems: Array<SideNavItem>;
  sideNavItemsMaps: Array<SideNavItemsMap>;
  currentMainFilterItems: Array<MainFilterItem>;

  limit = 100;

  currentSideNavItem: SideNavItem;

  showInsight = false;
  selectedSdk: SdkInsight;
  selectedDate: string;
  navigateTo: string;
  selectedGroup = 'app';
  searchParamModel: string;

  @ViewChild('sideNav') sideNav: ElementRef;

  constructor(
    route: ActivatedRoute,
    private _sdkIndexService: SdkIndexService
  ) {
    super(route);
    this.loading = false;
    this.loadingData = false;
    this.showLoadingMore = false;
    this.showLoadMore = false;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this._sdkIndexService.route = this._route;
    this.initComponent();

    this._route.queryParamMap.subscribe(params => {
      const name = params.get('name');
      if (name && name != this.selectedSdk.sdk) {
        this.openInsight(0, { sdk: this.selectedSdk.sdk });
      }
    });

    this._pageMetaService.setStructuredData(this.getStructuredData());
  }

  getStructuredData() {
    return {
      '@context': 'https://schema.org',
      name: "Pixalate's SDK Index",
      publisher: {
        '@type': 'Organization',
        name: 'Pixalate'
      }
    };
  }

  initComponent() {
    if (!this.loading && !this.loadingData) {
      const foundConfig = SDK_REPORT_CONFIG[0];
      this.currentConfig = foundConfig;
      this.currentMainFilterItems = foundConfig.mainFilterItems;

      this.loading = true;
      this.loadMonths();
    }

    this._sdkIndexService.setPageDocumentMeta(this.currentConfig);
  }

  openInsight(rowIdx, dataItem, navigateTo = 'overview') {
    this.navigateTo = navigateTo;
    this.selectedSdk = dataItem;

    const foundDFilter = this._sdkIndexService.getFilterSelected(
      this.currentMainFilterItems,
      'key',
      'date'
    );
    this.selectedDate = foundDFilter?.value;

    this.showInsight = true;
    this.trackTableRowClicked(rowIdx, dataItem);
    this._router.navigate(['/sdk-trust-index'], {
      queryParams: { name: dataItem.developerName }
    });
  }

  openInsightWithFilter(event, name) {
    if ('buttons' in event && event.buttons == 1) {
      this.searchParamModel = name;
      this.openInsight(0, { sdk: name });
      this.loadGridData();
    }
  }

  closeInsight() {
    this.showInsight = false;
    this.selectedSdk = null;
    this._router.navigate(['/sdk-trust-index']);
  }

  selectMainFilter(
    selectedMainFilterItem: MainFilterItem,
    option: DropdownItem
  ) {
    if (selectedMainFilterItem.selected.value !== option.value) {
      selectedMainFilterItem.selected = option;

      const promises = [];
      // Reload dependency Facets based on dependency key and group
      this.currentMainFilterItems.forEach(mainFilterItem => {
        if (
          mainFilterItem.dependencies &&
          mainFilterItem.dependencies.length > 0
        ) {
          const foundDepdendency = mainFilterItem.dependencies.some(
            dependency =>
              (dependency.key === selectedMainFilterItem.key ||
                dependency.label === selectedMainFilterItem.label) &&
              (!dependency.group || dependency.group === this.selectedGroup)
          );
          if (foundDepdendency) {
            promises.push(
              this._sdkIndexService.loadOptions(
                mainFilterItem,
                this.currentConfig,
                this.selectedGroup
              )
            );
          }
        }
      });

      void Promise.all(promises).then(() => this.loadGridData());
    }
  }

  loadMonths() {
    let mainFilterItems = this.currentConfig.mainFilterItems;
    mainFilterItems = mainFilterItems.filter(
      filter => !filter.groups || filter.groups.indexOf(this.selectedGroup) >= 0
    );
    mainFilterItems.forEach(
      (mainFilter, index) => (mainFilter.routeParam = `param${index + 1}`)
    );
    const monthFilterItem = mainFilterItems.filter(
      filter => filter.key === 'date'
    )[0];
    if (monthFilterItem && monthFilterItem.options.length <= 0) {
      this.loading = true;
      void this._sdkIndexService
        .loadMonthOptions(
          this.currentConfig,
          mainFilterItems,
          monthFilterItem,
          this.selectedGroup
        )
        .then(() => {
          this.loading = false;
          this.loadGridData();
        });
    } else {
      this.loading = false;
      this.loadGridData();
    }
  }

  loadMore() {
    this.loadGridData(true);
  }

  loadGridData(isLoadMore = false) {
    if (!isLoadMore) {
      this.start = 0;
      this.gridData = [];
    }

    this.loadingData = true;
    this.showLoadMore = false;
    this.showLoadingMore = isLoadMore;

    void this._sdkIndexService
      .getRankingsData(
        this.currentConfig,
        this.selectedGroup,
        this.start,
        this.limit
      )
      .then(response => {
        if (response && response['docs']) {
          this.showLoadingMore = false;
          this.gridData = this.gridData.concat(response['docs']);

          const dataCount = this.gridData.length;
          this.start = dataCount;
          this.noResult = dataCount === 0;
          this.showLoadMore = response['numFound'] > dataCount;

          if (isLoadMore) {
            this._tracking.eventTrack('sdk_load_more', {
              items_loaded: dataCount
            });
          } else {
            this._tracking.eventTrack('sdk_page_load', {
              url: window.location.href
            });
          }
        }
        setTimeout(() => {
          this.loading = false;
          this.loadingData = false;

          if (!this.isDestroyed) {
            // void this._router.navigate(routeParams, {
            //   relativeTo: this._route,
            //   queryParams: this._route.snapshot.queryParams,
            //   queryParamsHandling: 'merge'
            // });
          }
        }, 300);
      });
  }

  trackTableRowClicked(idx, data) {
    this._tracking.eventTrack('sdk_row_clicked', {
      row_index: idx + 1,
      row_data: data
    });
  }

  searchEvent(searchValue) {
    this.searchParamModel = searchValue;
  }
}
